import { formatDosage, formatSig } from "@/components/Medicine/utils";
import { formatDateTime, formatName, formatPatientAge } from "@/Utils/utils";

import PrintPreview from "@/CAREUI/misc/PrintPreview";
import { getPermissions } from "@/common/Permissions";
import { formatEncounterDateTime } from "@/components/Common/clinicalPrint";
import FacilityPrintHeader from "@/components/Common/FacilityPrintHeader";
import Loading from "@/components/Common/Loading";
import PrintFooter from "@/components/Common/PrintFooter";
import PrintTable from "@/components/Common/PrintTable";
import QuestionnaireResponsesList, {
  formatValue,
} from "@/components/Facility/ConsultationDetails/QuestionnaireResponsesList";
import { getFrequencyDisplay } from "@/components/Medicine/MedicationsTable";
import {
  getDisplayIdentifierValue,
  getIdentifiersWithUhidFirst,
} from "@/components/Patient/utils";
import { usePermissions } from "@/context/PermissionContext";
import diagnosisApi from "@/types/emr/diagnosis/diagnosisApi";
import encounterApi from "@/types/emr/encounter/encounterApi";
import { displayMedicationName } from "@/types/emr/medicationRequest/medicationRequest";
import medicationRequestApi from "@/types/emr/medicationRequest/medicationRequestApi";
import medicationStatementApi from "@/types/emr/medicationStatement/medicationStatementApi";
import patientApi from "@/types/emr/patient/patientApi";
import serviceRequestApi from "@/types/emr/serviceRequest/serviceRequestApi";
import symptomApi from "@/types/emr/symptom/symptomApi";
import facilityApi from "@/types/facility/facilityApi";
import { PatientIdentifierUse } from "@/types/patient/patientIdentifierConfig/patientIdentifierConfig";
import { Question } from "@/types/questionnaire/question";
import { QuestionnaireResponse } from "@/types/questionnaire/questionnaireResponse";
import { SchedulableResourceType } from "@/types/scheduling/schedule";
import query from "@/Utils/request/query";
import careConfig from "@careConfig";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { Loader } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatPhoneNumberIntl } from "react-phone-number-input";

interface TreatmentSummaryProps {
  facilityId?: string;
  encounterId: string;

  patientId: string;
}

const SectionLayout = ({
  children,
  title,
}: {
  title: string;
  children: React.ReactNode;
}) => {
  return (
    <section className="space-y-2">
      <h3 className="text-sm font-semibold leading-normal">{title}</h3>
      {children}
    </section>
  );
};

const isVitalsResponse = (response: QuestionnaireResponse) => {
  const questionnaire = response.questionnaire;
  if (!questionnaire) return false;

  return [
    questionnaire.slug,
    questionnaire.title,
    questionnaire.code?.code,
    questionnaire.code?.display,
  ].some((value) => value?.trim().toLowerCase().includes("vital"));
};

const doctorRolePattern =
  /\b(doctor|physician|consultant|surgeon|specialist|clinician|practitioner|provider|attending)\b/i;

const getAnsweredQuestions = (
  questions: Question[],
  response: QuestionnaireResponse,
): Question[] =>
  questions.flatMap((question) => {
    if (question.type === "group") {
      return getAnsweredQuestions(question.questions ?? [], response);
    }
    if (question.type === "structured" || question.type === "display") {
      return [];
    }

    const answer = response.responses.find(
      (item) => item.question_id === question.id,
    );
    const hasValue = answer?.values.some(
      (value) => value.value !== undefined || value.coding,
    );
    return hasValue ? [question] : [];
  });

const CompactVitalsResponse = ({
  response,
}: {
  response: QuestionnaireResponse;
}) => {
  const { t } = useTranslation();
  const questions = getAnsweredQuestions(
    response.questionnaire?.questions ?? [],
    response,
  );

  if (!questions.length) return null;

  return (
    <SectionLayout title={t("vitals")}>
      <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-4 print:grid-cols-4">
        {questions.map((question) => {
          const answer = response.responses.find(
            (item) => item.question_id === question.id,
          );

          return (
            <div
              key={question.id}
              className="rounded-sm border border-gray-200 px-2 py-1.5"
            >
              <div className="truncate text-xs text-gray-600">
                {question.text}
              </div>
              <div className="mt-0.5 text-sm font-semibold text-gray-950">
                {answer?.values.map((value, index) => (
                  <span key={index}>
                    {index > 0 && ", "}
                    {value.value !== undefined &&
                      formatValue(value.value, question.type)}
                    {(value.unit ?? question.unit) && (
                      <span className="ml-1 font-normal text-gray-600">
                        {(value.unit ?? question.unit)?.code}
                      </span>
                    )}
                    {value.coding?.display}
                  </span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </SectionLayout>
  );
};

export default function TreatmentSummary({
  facilityId,
  encounterId,
  patientId,
}: TreatmentSummaryProps) {
  const { t } = useTranslation();
  const summaryTitle = t("clinical_report");

  const { data: encounter, isLoading: encounterLoading } = useQuery({
    queryKey: ["encounter", encounterId],
    queryFn: query(encounterApi.get, {
      pathParams: { id: encounterId },
      queryParams: facilityId
        ? { facility: facilityId }
        : { patient: patientId },
    }),
    enabled: !!encounterId && !!patientId,
  });

  const { data: patient } = useQuery({
    queryKey: ["patient", patientId],
    queryFn: query(patientApi.get, {
      pathParams: {
        id: patientId,
      },
    }),
    enabled: !!patientId,
  });

  const resolvedFacilityId = facilityId ?? encounter?.facility?.id;
  const { data: facility, isLoading: facilityLoading } = useQuery({
    queryKey: ["facility", resolvedFacilityId],
    queryFn: query(facilityApi.get, {
      pathParams: { facilityId: resolvedFacilityId! },
    }),
    enabled: !!resolvedFacilityId,
  });

  const { hasPermission } = usePermissions();
  const { canViewClinicalData } = getPermissions(
    hasPermission,
    patient?.permissions ?? [],
  );
  const { canReadEncounter } = getPermissions(
    hasPermission,
    encounter?.permissions ?? [],
  );

  const canAccess = canReadEncounter || canViewClinicalData;

  const { data: symptoms, isLoading: symptomsLoading } = useQuery({
    queryKey: ["symptoms", patientId, encounterId],
    queryFn: query.paginated(symptomApi.listSymptoms, {
      pathParams: { patientId },
      queryParams: { encounter: encounterId },
      pageSize: 100,
    }),
    enabled: !!patientId && !!encounterId,
  });

  const { data: diagnoses, isLoading: diagnosesLoading } = useQuery({
    queryKey: ["diagnosis", patientId, encounterId],
    queryFn: query.paginated(diagnosisApi.listDiagnosis, {
      pathParams: { patientId },
      queryParams: {
        encounter: encounterId,
        category: "encounter_diagnosis,chronic_condition",
      },
      pageSize: 100,
    }),
    enabled: !!patientId && !!encounterId,
  });

  const { data: medications, isLoading: medicationsLoading } = useQuery({
    queryKey: ["medication_requests", patientId, encounterId],
    queryFn: query.paginated(medicationRequestApi.list, {
      pathParams: { patientId },
      queryParams: { encounter: encounterId, facility: facilityId },
      pageSize: 100,
    }),
    enabled: !!encounterId,
  });
  const { data: medicationStatement, isLoading: medicationStatementLoading } =
    useQuery({
      queryKey: ["medication_statements", patientId],
      queryFn: query.paginated(medicationStatementApi.list, {
        pathParams: { patientId },
        pageSize: 100,
      }),
      enabled: !!patientId,
    });

  const { data: serviceRequests, isLoading: serviceRequestsLoading } = useQuery(
    {
      queryKey: ["service_requests", patientId, encounterId],
      queryFn: query.paginated(serviceRequestApi.listServiceRequest, {
        pathParams: { facilityId: facilityId || "" },
        queryParams: {
          encounter: encounterId,
        },
        pageSize: 100,
      }),
      enabled: !!encounterId && !!facilityId,
    },
  );

  if (encounterLoading) {
    return <Loading />;
  }

  if (!encounter) {
    return (
      <div className="flex h-[200px] items-center justify-center rounded-lg border-2 border-gray-200 border-dashed p-4 text-gray-500">
        {t("no_patient_record_found")}
      </div>
    );
  }

  const isLoading =
    encounterLoading ||
    facilityLoading ||
    diagnosesLoading ||
    symptomsLoading ||
    medicationsLoading ||
    medicationStatementLoading ||
    serviceRequestsLoading;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center">
        <PrintPreview title={`${summaryTitle} - ${encounter.patient.name}`}>
          <div className="flex items-center justify-center h-full">
            <Loader />
          </div>
        </PrintPreview>
      </div>
    );
  }

  const identifiers = getIdentifiersWithUhidFirst(
    encounter.patient.instance_identifiers,
    encounter.patient.facility_identifiers,
    ({ config }) => config.config.use === PatientIdentifierUse.official,
  ).filter(
    (identifier, index, all) =>
      all.findIndex(
        (candidate) =>
          candidate.config.config.display ===
            identifier.config.config.display &&
          getDisplayIdentifierValue(candidate) ===
            getDisplayIdentifierValue(identifier),
      ) === index,
  );

  const doctorMembers = encounter.care_team.filter((member) =>
    doctorRolePattern.test(
      `${member.role?.code ?? ""} ${member.role?.display ?? ""}`,
    ),
  );
  const appointmentDoctor =
    encounter.appointment?.resource_type ===
    SchedulableResourceType.Practitioner
      ? formatName(encounter.appointment.resource)
      : null;
  const mainDoctors = [
    appointmentDoctor,
    ...doctorMembers.map((member) => formatName(member.member)),
  ].filter((name, index, names): name is string =>
    Boolean(name && name !== "-" && names.indexOf(name) === index),
  );
  const isOpd = encounter.encounter_class === "amb";

  return (
    <div className="flex justify-center items-center">
      <PrintPreview
        title={`${summaryTitle} - ${encounter.patient.name}`}
        patientId={patient?.id}
        patientEmail={patient?.email}
        patientName={encounter.patient.name}
      >
        {isOpd && <style>{"@page { size: A4 portrait; }"}</style>}
        <div className="mx-auto max-w-4xl rounded-lg border border-gray-200 p-6 shadow-sm print:mx-4 print:rounded-md print:border-gray-300 print:p-5 print:shadow-none">
          <div className="space-y-3">
            {/* Header */}
            <div className="flex justify-between items-start pb-2 border-b border-gray-200">
              {isOpd ? (
                <FacilityPrintHeader
                  facility={facility}
                  fallbackName={encounter.facility?.name}
                  title={summaryTitle}
                />
              ) : (
                <div className="min-w-0 flex-1">
                  <div>
                    <h1 className="text-2xl font-semibold leading-tight">
                      {encounter.facility?.name}
                    </h1>
                    {facility?.address && (
                      <p className="mt-0.5 max-w-2xl text-xs leading-snug text-gray-600">
                        {facility.address}
                      </p>
                    )}
                    <h2 className="mt-1 text-sm font-semibold !text-black">
                      {summaryTitle}
                    </h2>
                  </div>
                </div>
              )}
              {!isOpd && (
                <img
                  src={careConfig.mainLogo?.dark}
                  alt="Care Logo"
                  className="h-10 w-auto object-contain ml-6"
                  onError={(event) => {
                    const image = event.currentTarget;
                    image.onerror = null;
                    image.src =
                      "https://aayushnet.com/images/aayushnet_logo_light.png";
                  }}
                />
              )}
            </div>

            {/* Patient Details */}
            <div className="border-b border-gray-200 pb-3">
              <div className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                <div className="space-y-1.5">
                  {isOpd && mainDoctors.length > 0 && (
                    <div className="grid grid-cols-[10rem_auto_1fr] md:grid-cols-[8rem_auto_1fr] items-center gap-x-1">
                      <span className="text-gray-600">{t("consultant")}</span>
                      <span className="text-gray-600">:</span>
                      <span className="font-semibold break-words">
                        {mainDoctors.join(", ")}
                      </span>
                    </div>
                  )}
                  <div className="grid grid-cols-[10rem_auto_1fr] md:grid-cols-[8rem_auto_1fr] items-center gap-x-1">
                    <span className="text-gray-600">{t("patient")}</span>
                    <span className="text-gray-600">:</span>
                    <span className="font-semibold break-words">
                      {encounter.patient.name}
                    </span>
                  </div>
                  <div className="grid grid-cols-[10rem_auto_1fr] md:grid-cols-[8rem_auto_1fr] items-center gap-x-1">
                    <span className="text-gray-600">{`${t("age")} / ${t("sex")}`}</span>
                    <span className="text-gray-600">:</span>
                    <span className="font-semibold break-words">
                      {`${formatPatientAge(encounter.patient, true)}, ${t(`GENDER__${encounter.patient.gender}`)}`}
                    </span>
                  </div>
                  <div className="grid grid-cols-[10rem_auto_1fr] md:grid-cols-[8rem_auto_1fr] items-center gap-x-1">
                    <span className="text-gray-600">
                      {t("encounter_class")}
                    </span>
                    <span className="text-gray-600">:</span>
                    <span className="font-semibold">
                      {t(`encounter_class__${encounter.encounter_class}`)}
                    </span>
                  </div>
                  <div className="grid grid-cols-[10rem_auto_1fr] md:grid-cols-[8rem_auto_1fr] items-center gap-x-1">
                    <span className="text-gray-600">{t("priority")}</span>
                    <span className="text-gray-600">:</span>
                    <span className="font-semibold">
                      {encounter.priority
                        ? t(`encounter_priority__${encounter.priority}`)
                        : "-"}
                    </span>
                  </div>

                  {encounter.hospitalization?.admit_source && (
                    <div className="grid grid-cols-[10rem_auto_1fr] md:grid-cols-[8rem_auto_1fr] items-center gap-x-1">
                      <span className="text-gray-600">
                        {t("admission_source")}
                      </span>
                      <span className="text-gray-600">:</span>
                      <span className="font-semibold">
                        {t(
                          `encounter_admit_sources__${encounter.hospitalization.admit_source}`,
                        )}
                      </span>
                    </div>
                  )}
                  {encounter.hospitalization?.re_admission && (
                    <div className="grid grid-cols-[10rem_auto_1fr] md:grid-cols-[8rem_auto_1fr] items-center gap-x-1">
                      <span className="text-gray-600">{t("readmission")}</span>
                      <span className="text-gray-600">:</span>
                      <span className="font-semibold">{t("yes")}</span>
                    </div>
                  )}
                  {encounter.hospitalization?.diet_preference && (
                    <div className="grid grid-cols-[10rem_auto_1fr] md:grid-cols-[8rem_auto_1fr] items-center gap-x-1">
                      <span className="text-gray-600">
                        {t("diet_preference")}
                      </span>
                      <span className="text-gray-600">:</span>
                      <span className="font-semibold">
                        {t(
                          `encounter_diet_preference__${encounter.hospitalization.diet_preference}`,
                        )}
                      </span>
                    </div>
                  )}
                </div>

                {/* Right Column */}
                <div className="space-y-1.5">
                  {identifiers.map((identifier) => (
                    <div
                      key={identifier.config.id}
                      className="grid grid-cols-[10rem_auto_1fr] md:grid-cols-[8rem_auto_1fr] items-center gap-x-1"
                    >
                      <span className="text-gray-600">
                        {identifier.config.config.display}
                      </span>
                      <span className="text-gray-600">:</span>
                      <span className="font-semibold">
                        {getDisplayIdentifierValue(identifier)}
                      </span>
                    </div>
                  ))}

                  <div className="grid grid-cols-[10rem_auto_1fr] md:grid-cols-[8rem_auto_1fr] items-center gap-x-1">
                    <span className="text-gray-600">{t("mobile_number")}</span>
                    <span className="text-gray-600">:</span>
                    <span className="font-semibold break-words">
                      {encounter.patient.phone_number &&
                        formatPhoneNumberIntl(encounter.patient.phone_number)}
                    </span>
                  </div>

                  {encounter.period?.start && (
                    <div className="grid grid-cols-[10rem_auto_1fr] md:grid-cols-[8rem_auto_1fr] items-center gap-x-1">
                      <span className="text-gray-600">
                        {t(isOpd ? "encounter_date_time" : "encounter_date")}
                      </span>
                      <span className="text-gray-600">:</span>
                      <span className="font-semibold">
                        {isOpd
                          ? formatEncounterDateTime(encounter.period.start)
                          : format(
                              new Date(encounter.period.start),
                              "dd MMM yyyy, EEEE",
                            )}
                      </span>
                    </div>
                  )}

                  <div className="grid grid-cols-[10rem_auto_1fr] md:grid-cols-[8rem_auto_1fr] items-center gap-x-1">
                    <span className="text-gray-600">{t("status")}</span>
                    <span className="text-gray-600">:</span>
                    <span className="font-semibold">
                      {t(`encounter_status__${encounter.status}`)}
                    </span>
                  </div>

                  {encounter.external_identifier && (
                    <div className="grid grid-cols-[10rem_auto_1fr] md:grid-cols-[8rem_auto_1fr] items-center gap-x-1">
                      <span className="text-gray-600">{t("visit_id")}</span>
                      <span className="text-gray-600">:</span>
                      <span className="font-semibold">
                        {encounter.external_identifier}
                      </span>
                    </div>
                  )}

                  {encounter.hospitalization?.discharge_disposition && (
                    <div className="grid grid-cols-[10rem_auto_1fr] md:grid-cols-[8rem_auto_1fr] items-center gap-x-1">
                      <span className="text-gray-600">
                        {t("discharge_disposition")}
                      </span>
                      <span className="text-gray-600">:</span>
                      <span className="font-semibold">
                        {t(
                          `encounter_discharge_disposition__${encounter.hospitalization.discharge_disposition}`,
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Care Team Section */}
            <div className="space-y-2">
              {encounter.care_team.length > 0 && (
                <div className="space-y-4">
                  {/* Other Consultants */}
                  {encounter.care_team.length > 1 && (
                    <div className="border border-gray-100 p-3 rounded-sm">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        {t("care_team")}
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {encounter.care_team.map((member, index) => (
                          <div key={index} className="flex items-start">
                            <div className="min-w-0 flex-1">
                              <div className="font-semibold truncate">
                                {formatName(member.member)}
                              </div>
                              {member.role?.display && (
                                <div className="text-sm text-gray-500 truncate">
                                  {member.role.display}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Medical Information */}
            <div className="space-y-3">
              {/* Vitals */}
              <QuestionnaireResponsesList
                encounterId={encounterId}
                patientId={encounter.patient.id}
                isPrintPreview
                onlyUnstructured
                canAccess={canAccess}
                filterResponses={isVitalsResponse}
                showEmptyState={false}
                renderItem={(response) => (
                  <CompactVitalsResponse response={response} />
                )}
              />

              {/* Symptoms */}
              {symptoms?.count != 0 && (
                <SectionLayout title={t("symptoms")}>
                  {symptoms?.results
                    .filter((item) => item.code.system === "local")
                    .map((item) => (
                      <p
                        key={item.id}
                        className="whitespace-pre-wrap break-words"
                      >
                        {item.code.display || item.code.code}
                      </p>
                    ))}
                  <PrintTable
                    hideEmptyColumns
                    headers={[
                      { key: "symptom" },
                      { key: "severity" },
                      { key: "status" },
                      { key: "verification" },
                      { key: "onset" },
                      { key: "notes" },
                    ]}
                    rows={symptoms?.results
                      ?.filter((item) => item.code.system !== "local")
                      .map((symptom) => ({
                        symptom: symptom.code.display,
                        severity: t(symptom.severity),
                        status: t(symptom.clinical_status),
                        verification: t(symptom.verification_status),
                        onset: symptom.onset?.onset_datetime
                          ? new Date(
                              symptom.onset.onset_datetime,
                            ).toLocaleDateString()
                          : "-",
                        notes: symptom.note,
                      }))}
                  />
                </SectionLayout>
              )}

              {/* Diagnoses */}
              {diagnoses?.count != 0 && (
                <SectionLayout title={t("diagnoses")}>
                  {diagnoses?.results
                    .filter((item) => item.code.system === "local")
                    .map((item) => (
                      <p
                        key={item.id}
                        className="whitespace-pre-wrap break-words"
                      >
                        {item.code.display || item.code.code}
                      </p>
                    ))}
                  <PrintTable
                    hideEmptyColumns
                    headers={[
                      { key: "diagnosis" },
                      { key: "status" },
                      { key: "severity" },
                      { key: "verification" },
                      { key: "onset" },
                      { key: "notes" },
                    ]}
                    rows={diagnoses?.results
                      .filter((item) => item.code.system !== "local")
                      .map((diagnosis) => ({
                        diagnosis: diagnosis.code.display,
                        status: t(diagnosis.clinical_status),
                        severity: diagnosis.severity
                          ? t(diagnosis.severity)
                          : "-",
                        verification: t(diagnosis.verification_status),
                        onset: diagnosis.onset?.onset_datetime
                          ? new Date(
                              diagnosis.onset.onset_datetime,
                            ).toLocaleDateString()
                          : undefined,
                        notes: diagnosis.note,
                      }))}
                  />
                </SectionLayout>
              )}

              {/* Medications */}
              {medications?.count != 0 && (
                <SectionLayout title={t("medications")}>
                  <PrintTable
                    hideEmptyColumns
                    headers={[
                      { key: "medicine", widthPercent: 22 },
                      { key: "status", widthPercent: 10 },
                      { key: "dosage", widthPercent: 10 },
                      { key: "frequency", widthPercent: 18 },
                      { key: "duration", widthPercent: 12 },
                      { key: "instructions", widthPercent: 28 },
                    ]}
                    rows={medications?.results.map((medication) => {
                      const instruction = medication.dosage_instruction[0];
                      const additionalInstructions =
                        instruction?.additional_instruction
                          ?.map((item) => item.display)
                          .filter(Boolean)
                          .join(", ");
                      const frequency = getFrequencyDisplay(
                        instruction?.timing,
                      );
                      const dosage = formatDosage(instruction);
                      const duration =
                        instruction?.timing?.repeat?.bounds_duration;
                      const remarks = formatSig(instruction);
                      const notes = medication.note;
                      return {
                        medicine: displayMedicationName(medication),
                        status: t(`medication_status__${medication.status}`),
                        dosage: dosage,
                        frequency: instruction?.as_needed_boolean
                          ? `${t("as_needed_prn")} (${instruction?.as_needed_for?.display ?? "-"})` +
                            (instruction?.additional_instruction?.length
                              ? `, ${additionalInstructions}`
                              : "")
                          : `${frequency?.meaning ?? "-"}${
                              instruction?.additional_instruction?.length
                                ? `, ${additionalInstructions}`
                                : ""
                            }`,
                        duration: duration
                          ? `${duration.value} ${duration.unit}`
                          : "-",
                        instructions: `${remarks || "-"}${notes ? ` (${t("note")}: ${notes})` : ""}`,
                      };
                    })}
                  />
                </SectionLayout>
              )}

              {/* Medication Statements */}
              {medicationStatement?.count != 0 && (
                <SectionLayout title={t("medication_statements")}>
                  <PrintTable
                    hideEmptyColumns
                    headers={[
                      { key: "medication" },
                      { key: "dosage" },
                      { key: "status" },
                      {
                        key: "medication_taken_between",
                      },
                      { key: "reason" },
                      { key: "notes" },
                    ]}
                    rows={medicationStatement?.results.map((medication) => ({
                      medication:
                        medication.medication.display ??
                        medication.medication.code,
                      dosage: medication.dosage_text,
                      status: t(`medication_status__${medication.status}`),
                      medication_taken_between: [
                        medication.effective_period?.start,
                        medication.effective_period?.end,
                      ]
                        .map((date, ind) =>
                          date
                            ? formatDateTime(date)
                            : ind === 1
                              ? t("ongoing")
                              : "",
                        )
                        .join(" - "),
                      reason: medication.reason,
                      notes: medication.note,
                    }))}
                  />
                </SectionLayout>
              )}

              {/* Service Requests */}
              {serviceRequests?.count != 0 && (
                <SectionLayout title={t("service_requests")}>
                  <PrintTable
                    hideEmptyColumns
                    headers={[
                      { key: "title" },
                      { key: "service_type" },
                      { key: "status" },
                      { key: "priority" },
                    ]}
                    rows={serviceRequests?.results.map((request) => ({
                      title: request.title,
                      service_type: t(request.category),
                      status: t(request.status),
                      priority: t(request.priority),
                    }))}
                  />
                </SectionLayout>
              )}
            </div>

            {/* Questionnaire Responses Section */}
            <div>
              <QuestionnaireResponsesList
                encounterId={encounterId}
                patientId={encounter.patient.id}
                isPrintPreview={true}
                onlyUnstructured={true}
                canAccess={canAccess}
                filterResponses={(response) => !isVitalsResponse(response)}
                showEmptyState={false}
                showResponseMetadata={false}
              />
            </div>
          </div>

          {encounter.discharge_summary_advice && (
            <SectionLayout title={t("clinical_report_advice")}>
              <p className="whitespace-pre-wrap break-words">
                {encounter.discharge_summary_advice}
              </p>
            </SectionLayout>
          )}

          {/* Footer */}
          <PrintFooter
            className="gap-4 [&>p]:whitespace-nowrap"
            leftContent={
              !isOpd && mainDoctors.length ? (
                <span>
                  <span className="font-semibold">{t("doctor")}: </span>
                  <span>{mainDoctors.join(", ")}</span>
                </span>
              ) : undefined
            }
          />
        </div>
      </PrintPreview>
    </div>
  );
}
